// EligibilityRequirements.js - New Zealand
import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, FileText, Briefcase, DollarSign, Languages, GraduationCap } from 'lucide-react';

const EligibilityRequirements = () => {
  const requirements = [
    {
      icon: Briefcase,
      title: 'Your Job or Job Offer',
      description: 'You need work, or an offer of work, in a skilled occupation at ANZSCO skill levels 1-3. The role must be genuine, full time, and paid at or above the median wage of NZD $31.61 an hour. The employer will be checked.',
      applicable: 'SMC, Work to Residence'
    },
    {
      icon: Languages,
      title: 'English Requirement',
      description: 'IELTS 6.5 overall with nothing below 6.0 in any band, or an accepted equivalent such as TOEFL iBT 79, PTE Academic 58 or OET Grade B. Results remain valid for 2 years.',
      applicable: 'All Programs'
    },
    {
      icon: GraduationCap,
      title: 'Recognised Qualifications',
      description: 'Your qualification has to be recognised and relevant to the occupation. Bachelor degrees score 40 points, master\'s degrees 50 and doctorates 70, with bonus points where the study was done in New Zealand.',
      applicable: 'SMC'
    },
    {
      icon: CheckCircle2,
      title: 'Points in the Pool',
      description: 'The pool opens at 160 points and selection usually starts at 180. Age, qualifications, work experience, a job offer and any New Zealand experience all feed into the figure.',
      applicable: 'SMC'
    },
    {
      icon: DollarSign,
      title: 'Age Bands',
      description: 'The 20-39 band earns the maximum 30 points. It falls from there: 20 points for 40-44, 10 points for 45-49 and 5 points for 50-55. You must be under 56 when you apply.',
      applicable: 'SMC'
    },
    {
      icon: FileText,
      title: 'Health and Character',
      description: 'A medical examination is compulsory, as are police certificates covering each country you have lived in for 12 months or more. Serious criminal convictions will block an application.',
      applicable: 'All Programs'
    }
  ];

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-semibold text-primary mb-4">
            The Requirements in Brief
          </h2>
          <p className="text-gray-600 text-base font-light max-w-3xl mx-auto">
            Every category sets its own detailed rules, but these are the criteria that surface in almost any New Zealand residence application.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {requirements.map((requirement, index) => {
            const Icon = requirement.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-surface p-6 rounded-lg border border-gray-200 hover:border-secondary transition-colors"
              >
                <div className="flex items-start gap-4 mb-4">
                  <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon size={20} className="text-primary" />
                  </div>
                  <div>
                    <h3 className="text-base font-semibold text-primary mb-1">
                      {requirement.title}
                    </h3>
                    <span className="text-xs text-secondary font-light">
                      {requirement.applicable}
                    </span>
                  </div>
                </div>
                <p className="text-gray-600 text-sm font-light leading-relaxed">
                  {requirement.description}
                </p>
              </motion.div>
            );
          })}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.8 }}
          className="mt-12 bg-gradient-to-r from-primary to-secondary rounded-lg p-8 text-white"
        >
          <h3 className="text-xl font-semibold mb-4">
            How SMC Points Are Awarded
          </h3>
          <p className="text-white/90 text-sm font-light leading-relaxed mb-4">
            You need 160 points to enter the pool, and in practice selection tends to begin at 180.
          </p>
          <div className="grid md:grid-cols-4 gap-4 text-sm">
            <div className="bg-white/10 rounded-lg p-4">
              <p className="font-semibold mb-1">Age (20-39)</p>
              <p className="text-white/80 font-light">30 points</p>
            </div>
            <div className="bg-white/10 rounded-lg p-4">
              <p className="font-semibold mb-1">PhD</p>
              <p className="text-white/80 font-light">70 points</p>
            </div>
            <div className="bg-white/10 rounded-lg p-4">
              <p className="font-semibold mb-1">Skilled Job in NZ</p>
              <p className="text-white/80 font-light">50-60 points</p>
            </div>
            <div className="bg-white/10 rounded-lg p-4">
              <p className="font-semibold mb-1">Partner Points</p>
              <p className="text-white/80 font-light">Up to 20 points</p>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 1 }}
          className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-6"
        >
          <p className="text-gray-700 text-sm font-light leading-relaxed">
            <strong>Note:</strong> thresholds and selection settings shift as immigration targets change. We measure your profile against the rules currently in force and advise on the category that suits you best.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default EligibilityRequirements;
